import { useState, FormEvent, ChangeEvent, useEffect } from "react";
import { useDispatch } from "react-redux";
import debounce from "lodash/debounce";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { SelectOption } from "@idw-types/common";
import { AxnAdminSecurityRule } from "@idw-types/data/management-console/axnadmin";
import { DefaultModal } from "../../common/modals/default-modal/default-modal";
import { InputFormGroup } from "../../common/input-form-group/input-form-group";
import { ButtonWithSpinner } from "../../common/button-with-spinner/button-with-spinner";
import {
    SecurityRuleStatusSelectFormGroup,
    SecurityRuleOperationTypeSelectFormGroup
} from "../../common/select-form-groups";
import { TextareaFormGroup } from "../../common/textarea-form-group/textarea-form-group";
import { resources } from "@constants/hardcoded-strings";
import { createSecurityRuleReduxRequest } from "@redux-requests/security-rules";
import styles from "./add-security-rule-modal.less";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ButtonTypes } from "@enums/button-types";

type AddSecurityRuleModalProps = {
    onModalClose: () => void;
    securityRules: Array<AxnAdminSecurityRule>;
    securityRule?: AxnAdminSecurityRule
};

type AddSecurityRuleModalState = {
    requestInProgress: boolean;
    securityRule: AxnAdminSecurityRule;
    selectedOperationTypeOption: SelectOption;
    selectedStatusOption: SelectOption;
};

const defaultSecurityRule: AxnAdminSecurityRule = {
    id: "",
    className: "",
    operationType: "",
    pathText: "",
    rule: "",
    ruleName: "",
    status: ""
};

export const AddSecurityRuleModal = (props: AddSecurityRuleModalProps) => {
    const dispatch = useDispatch();
    const { onModalClose, securityRules, securityRule } = props;
    const ruleToUse = securityRule || defaultSecurityRule;
    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const [state, setState] = useState<AddSecurityRuleModalState>({
        requestInProgress: false,
        securityRule: ruleToUse,
        selectedOperationTypeOption: ruleToUse.operationType ? {
            label: ruleToUse.operationType,
            value: ruleToUse.operationType
        } : null,
        selectedStatusOption: ruleToUse.status ? {
            label: ruleToUse.status,
            value: ruleToUse.status
        } : null
    });

    const [idCheckMessage, setIdCheckMessage] = useState("");

    const checkSecurityRuleId = debounce(() => {
        if (
            (securityRule && securityRule.id !== state.securityRule.id && state.securityRule.id !== "") ||
            !securityRule && state.securityRule.id !== ""
        ) {
            const existingRule = securityRules.find(sr => sr.id === state.securityRule.id);
            if (existingRule) {
                setIdCheckMessage("ID Already Exists!");
            } else {
                setIdCheckMessage("ID is available!");
            }
        } else {
            setIdCheckMessage("");
        }
    }, 300);

    useEffect(() => {
        setIdCheckMessage("Checking...");
        if (state.securityRule.id !== "") {
            checkSecurityRuleId();
        } else {
            setIdCheckMessage("");
        }
    }, [state.securityRule.id]);

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setState({ ...state, requestInProgress: true });

        const newSecurityRule = await createSecurityRuleReduxRequest(dispatch)(state.securityRule);
        if (newSecurityRule) {
            onModalClose();
        } else {
            setState({ ...state, requestInProgress: false });
        }
    };

    const handleUpdateId = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            securityRule: {
                ...state.securityRule,
                id: event.target.value
            }
        });
    };

    const handleUpdateClassName = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            securityRule: {
                ...state.securityRule,
                className: event.target.value
            }
        });
    };

    const handleUpdatePathText = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            securityRule: {
                ...state.securityRule,
                pathText: event.target.value
            }
        });
    };

    const handleUpdateSecurityRuleOperationType = (newValue: SelectOption) => {
        if (newValue) {
            setState({
                ...state,
                securityRule: {
                    ...state.securityRule,
                    operationType: newValue.value,
                },
                selectedOperationTypeOption: newValue
            });
        }
    };

    const handleUpdateSecurityRuleStatus = (newValue: SelectOption) => {
        if (newValue) {
            setState({
                ...state,
                securityRule: {
                    ...state.securityRule,
                    status: newValue.value
                },
                selectedStatusOption: newValue
            });
        }
    };

    const handleUpdateRuleName = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            securityRule: {
                ...state.securityRule,
                ruleName: event.target.value
            }
        });
    };

    const handleUpdateRule = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            securityRule: {
                ...state.securityRule,
                rule: event.target.value
            }
        });
    };

    return (
        <DefaultModal
            automationId="add-security-rule-modal"
            ariaLabelledBy="add-security-rule-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={false}
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header id={"add-security-rule-title"} closeButton>
                    <Modal.Title>{resources.modals.addSecurityRule.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <InputFormGroup
                        controlId="security-rule-id-input"
                        label={resources.modals.addSecurityRule.inputs.id.label}
                        ariaLabel={resources.modals.addSecurityRule.inputs.id.label}
                        automationId="security-rule-id-input"
                        placeholder={resources.modals.addSecurityRule.inputs.id.placeholder}
                        value={state.securityRule.id}
                        onChange={handleUpdateId}
                        disabled={state.requestInProgress}
                        isRequired={true}
                        labelWidth={labelWidth}
                    />
                    {idCheckMessage && (
                        <div className={styles.idCheckMessage}>{idCheckMessage}</div>
                    )}
                    <InputFormGroup
                        controlId="security-rule-class-name-input"
                        label={resources.modals.addSecurityRule.inputs.className.label}
                        ariaLabel={resources.modals.addSecurityRule.inputs.className.label}
                        automationId="security-rule-class-name-input"
                        placeholder={resources.modals.addSecurityRule.inputs.className.placeholder}
                        value={state.securityRule.className || ""}
                        onChange={handleUpdateClassName}
                        disabled={state.requestInProgress}
                        isRequired={false}
                        labelWidth={labelWidth}
                    />
                    <InputFormGroup
                        controlId="security-rule-path-text-input"
                        label={resources.modals.addSecurityRule.inputs.pathText.label}
                        ariaLabel={resources.modals.addSecurityRule.inputs.pathText.label}
                        automationId="security-rule-path-text-input"
                        placeholder={resources.modals.addSecurityRule.inputs.pathText.placeholder}
                        value={state.securityRule.pathText || ""}
                        onChange={handleUpdatePathText}
                        disabled={state.requestInProgress}
                        isRequired={false}
                        labelWidth={labelWidth}
                    />
                    <SecurityRuleOperationTypeSelectFormGroup
                        idPrefix="management-console"
                        onChange={handleUpdateSecurityRuleOperationType}
                        selectedOption={state.selectedOperationTypeOption}
                        disabled={state.requestInProgress}
                        isRequired={true}
                        isSearchable={false}
                        isClearable={false}
                        labelWidth={labelWidth}
                    />
                    <SecurityRuleStatusSelectFormGroup
                        idPrefix="management-console"
                        onChange={handleUpdateSecurityRuleStatus}
                        selectedOption={state.selectedStatusOption}
                        disabled={state.requestInProgress}
                        isRequired={true}
                        isSearchable={false}
                        isClearable={false}
                        labelWidth={labelWidth}
                    />
                    <InputFormGroup
                        controlId="security-rule-rule-name-input"
                        label={resources.modals.addSecurityRule.inputs.ruleName.label}
                        ariaLabel={resources.modals.addSecurityRule.inputs.ruleName.label}
                        automationId="security-rule-rule-name-input"
                        placeholder={resources.modals.addSecurityRule.inputs.ruleName.placeholder}
                        value={state.securityRule.ruleName || ""}
                        onChange={handleUpdateRuleName}
                        disabled={state.requestInProgress}
                        isRequired={false}
                        labelWidth={labelWidth}
                    />
                    <TextareaFormGroup
                        controlId="security-rule-rule-input"
                        label={resources.modals.addSecurityRule.inputs.rule.label}
                        ariaLabel={resources.modals.addSecurityRule.inputs.rule.label}
                        automationId="security-rule-rule-input"
                        placeholder={resources.modals.addSecurityRule.inputs.rule.placeholder}
                        value={state.securityRule.rule || ""}
                        onChange={handleUpdateRule}
                        disabled={state.requestInProgress}
                        isRequired={false}
                        labelWidth={labelWidth}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button id="add-security-cancel-button" variant="secondary" onClick={onModalClose}>{resources.common.buttons.cancel.label}</Button>
                    <ButtonWithSpinner
                        id="add-security-save-button"
                        disabled={state.requestInProgress}
                        hideSpinner={false}
                        variant="primary"
                        type={ButtonTypes.SUBMIT}
                        label={resources.common.buttons.save.label}
                    />
                </Modal.Footer>
            </Form>
        </DefaultModal>
    );
};